import React, { useState } from 'react';
import { Key, Mail, Lock, User, ArrowLeft, Eye, EyeOff, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import TwoFactorVerify from '../components/TwoFactorVerify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const navigate = useNavigate();
  const { checkAuth, setUser } = useAuth();
  const [mode, setMode] = useState('select'); // 'select', 'login', 'register', 'forgot', '2fa'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSocialLogin = (provider) => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&provider=${provider}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      // Check if 2FA is required (already configured)
      if (response.data.requires_2fa) {
        setMode('2fa');
        toast.info('Digite o código do seu autenticador');
        setLoading(false);
        return;
      }
      
      // Check if 2FA setup is required (mandatory for all users)
      if (response.data.requires_2fa_setup) {
        setUser(response.data.user);
        toast.warning('Configure a autenticação de dois fatores para continuar', { duration: 5000 });
        navigate('/configurar-2fa', { 
          state: { 
            user: response.data.user, 
            mandatory: true,
            setupToken: response.data.setup_token  // Pass token via state
          }, 
          replace: true 
        });
        setLoading(false);
        return;
      }
      
      // Set user in context
      if (response.data.user) {
        setUser(response.data.user);
      }
      
      // Check if should setup 2FA (warning only, not mandatory)
      if (response.data.should_setup_2fa) {
        toast.warning(response.data.two_fa_warning, { duration: 5000 });
      } else {
        toast.success(response.data.message);
      }
      
      // Navigate to entrada with user state for immediate ProtectedRoute recognition
      navigate('/entrada', { state: { user: response.data.user }, replace: true });
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      
      // Check if captcha is required
      if (typeof errorDetail === 'object' && errorDetail?.require_captcha) {
        toast.error(`Muitas tentativas de login (${errorDetail.attempts}). Por favor, aguarde.`);
      } else {
        toast.error(errorDetail || 'Erro ao fazer login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle 2FA verification success
  const handle2FASuccess = (user) => {
    setUser(user);
    navigate('/entrada', { state: { user }, replace: true });
  };

  // Handle 2FA cancel
  const handle2FACancel = () => {
    setMode('login');
  };

  // If in 2FA mode, show 2FA verification component
  if (mode === '2fa') {
    return (
      <TwoFactorVerify 
        onSuccess={handle2FASuccess}
        onCancel={handle2FACancel}
      />
    );
  }

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Preencha todos os campos');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('As senhas não conferem');
      return;
    }
    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        { name, email, password },
        { withCredentials: true }
      );
      
      // Set user in context
      if (response.data.user) {
        setUser(response.data.user);
      }
      
      toast.success(response.data.message);
      
      // Navigate to entrada with user state for immediate ProtectedRoute recognition
      navigate('/entrada', { state: { user: response.data.user }, replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Digite seu email');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/forgot-password`,
        { 
          email,
          app_domain: window.location.origin  // Send current domain to backend
        },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setMode('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar email');
    } finally {
      setLoading(false);
    }
  };

  const renderBackButton = () => (
    <button
      onClick={() => {
        setMode('select');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setName('');
      }}
      className="flex items-center text-muted-foreground hover:text-foreground transition-colors mb-4"
    >
      <ArrowLeft size={16} className="mr-1" />
      Voltar
    </button>
  );

  return (
    <div 
      className="min-h-screen flex items-center justify-center"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1570626742839-59acd9822944)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60"></div>
      
      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-card/95 backdrop-blur-sm rounded-lg shadow-xl p-8 border border-border/40">
          
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <Key className="w-12 h-12 text-amber-500" />
            </div>
            <h1 className="text-3xl font-serif font-normal mb-2">Author's Key</h1>
            <p className="text-muted-foreground">
              {mode === 'select' && 'Entre no seu ateliê literário'}
              {mode === 'login' && 'Acesse sua conta'}
              {mode === 'register' && 'Crie sua conta'}
              {mode === 'forgot' && 'Recuperar senha'}
            </p>
          </div>

          {/* Mode: Select */}
          {mode === 'select' && (
            <div className="space-y-3">
              {/* Email/Password Option */}
              <Button
                onClick={() => setMode('login')}
                className="w-full h-12 text-base bg-amber-500 hover:bg-amber-600 text-stone-900"
                data-testid="email-login-button"
              >
                <Mail className="w-5 h-5 mr-2" />
                Entrar com Email
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border/40" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">ou continue com</span>
                </div>
              </div>

              {/* Social Logins */}
              <Button
                onClick={() => handleSocialLogin('google')}
                className="w-full h-11 text-base bg-white hover:bg-gray-100 text-gray-900 border border-gray-300"
                data-testid="google-login-button"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </Button>

              <Button
                onClick={() => handleSocialLogin('apple')}
                className="w-full h-11 text-base bg-black hover:bg-gray-900 text-white"
                data-testid="apple-login-button"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Apple
              </Button>

              <div className="text-center text-sm text-muted-foreground pt-4">
                <p>Ao continuar, você concorda com nossos</p>
                <p>Termos de Serviço e Política de Privacidade</p>
              </div>
            </div>
          )}

          {/* Mode: Login */}
          {mode === 'login' && (
            <div>
              {renderBackButton()}
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="seu@email.com"
                      className="pl-10"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-stone-900"
                  data-testid="login-submit"
                >
                  {loading ? 'Entrando...' : 'Entrar'}
                </Button>
              </form>
              
              <div className="mt-4 text-center space-y-2">
                <button
                  onClick={() => setMode('forgot')}
                  className="text-sm text-amber-600 hover:text-amber-700"
                >
                  Esqueci minha senha
                </button>
                <p className="text-sm text-muted-foreground">
                  Não tem conta?{' '}
                  <button
                    onClick={() => setMode('register')}
                    className="text-amber-600 hover:text-amber-700 font-medium"
                  >
                    Criar conta
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* Mode: Register */}
          {mode === 'register' && (
            <div>
              {renderBackButton()}
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Nome</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Seu nome"
                      className="pl-10"
                      data-testid="name-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="seu@email.com"
                      className="pl-10"
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="pl-10 pr-10"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Confirmar senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Digite a senha novamente"
                      className="pl-10"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-stone-900"
                  data-testid="register-submit"
                >
                  {loading ? 'Criando conta...' : 'Criar conta'}
                </Button>
              </form>
              
              <p className="mt-4 text-center text-sm text-muted-foreground">
                Já tem conta?{' '}
                <button
                  onClick={() => setMode('login')}
                  className="text-amber-600 hover:text-amber-700 font-medium"
                >
                  Entrar
                </button>
              </p>
            </div>
          )}

          {/* Mode: Forgot Password */}
          {mode === 'forgot' && (
            <div>
              {renderBackButton()}
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Digite seu email e enviaremos um link para redefinir sua senha.
                </p>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="seu@email.com"
                      className="pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-stone-900"
                  data-testid="forgot-submit"
                >
                  {loading ? 'Enviando...' : 'Enviar link de recuperação'}
                </Button>
              </form>
              
              <p className="mt-4 text-center text-sm text-muted-foreground">
                Lembrou a senha?{' '}
                <button
                  onClick={() => setMode('login')}
                  className="text-amber-600 hover:text-amber-700 font-medium"
                >
                  Voltar ao login
                </button>
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
